import {
  Description,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Fragment, type PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import { CircleButton } from './CircleButton';
import { Stack } from './Stack';

type Props = PropsWithChildren<{
  className?: string;
  closeButton?: boolean;
  description?: React.ReactNode;
  onClose: () => void;
  open: boolean;
  overlayClose?: boolean;
  title: React.ReactNode;
  titleClassName?: string;
}>;

export function Modal({
  open,
  description,
  title,
  onClose,
  closeButton = false,
  children,
  className = '',
  titleClassName = '',
  overlayClose = true,
}: Props): React.ReactElement {
  const classes = twMerge(
    'mx-auto max-w-5xl bg-white p-6 text-center font-aribau relative',
    className,
  );
  const titleClasses = twMerge('w-full', titleClassName);

  const handleCloseEvent = () => {
    if (overlayClose) {
      onClose();
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog onClose={handleCloseEvent} className="relative z-10">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <DialogBackdrop className="fixed inset-0 bg-black/50" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className={classes}>
                {closeButton && (
                  <CircleButton
                    as="button"
                    onClick={onClose}
                    className="absolute top-5 right-5 text-zinc-500 cursor-pointer"
                  >
                    <XMarkIcon className="size-5" />
                  </CircleButton>
                )}

                <Stack gap={6}>
                  <Stack direction="row" className="justify-between items-center">
                    <DialogTitle as="div" className={titleClasses}>
                      {title}
                    </DialogTitle>
                  </Stack>

                  {description && (
                    <Description className="font-aribaubold mt-5 text-4xl">
                      {description}
                    </Description>
                  )}

                  {children}
                </Stack>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
